import { useEffect, useState } from "react";
import { Home } from "lucide-react";

import zillowLogo from "@/shared/assets/loading/zillow.svg";
import redfinLogo from "@/shared/assets/connect_account_icons/redfin.png";
import carmaxLogo from "@/shared/assets/connect_account_icons/carmax.png";
import kbbLogo from "@/shared/assets/connect_account_icons/kbb.jpg";
import carfaxLogo from "@/shared/assets/connect_account_icons/carfax.jpg";

const loadingMessagesMap = {
  "real-estate": [
    "Analyzing property data...",
    "Comparing market values...",
    "Calculating estimate...",
  ],
  vehicle: ["Analyzing vehicle data...", "Checking market values...", "Calculating estimate..."],
};

const logosMap = {
  "real-estate": [zillowLogo, redfinLogo, "house-icon"],
  vehicle: [carmaxLogo, kbbLogo, carfaxLogo],
};

interface EstimateLoadingAnimationProps {
  assetType?: "real-estate" | "vehicle";
}

export function EstimateLoadingAnimation({
  assetType = "real-estate",
}: EstimateLoadingAnimationProps) {
  const [messageIndex, setMessageIndex] = useState(0);
  const [fade, setFade] = useState(true);

  const loadingMessages = loadingMessagesMap[assetType];
  const logos = logosMap[assetType];

  useEffect(() => {
    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setMessageIndex((prev) => (prev + 1) % loadingMessages.length);
        setFade(true);
      }, 150);
    }, 1500);

    return () => clearInterval(interval);
  }, [loadingMessages.length]);

  return (
    <div className="flex flex-col items-center justify-center py-24 gap-6">
      <div className="flex items-center">
        {logos.map((logo, index) => (
          <div key={index} className="flex items-center">
            {index > 0 && (
              <div className="relative h-[2px] w-10 bg-stone-200 overflow-hidden">
                <div
                  className="absolute h-full w-full bg-brand origin-left"
                  style={{
                    animation: `line-sweep 1.8s ease-in-out ${0.4 + index * 0.3}s infinite`,
                  }}
                />
              </div>
            )}
            {logo === "house-icon" ? (
              <div
                className="flex h-[76px] w-[76px] items-center justify-center rounded-full bg-white"
                style={{
                  animation: `logo-glow-filter 2.4s ease-in-out ${index * 0.8}s infinite`,
                }}
              >
                <Home className="h-8 w-8 text-stone-400" aria-hidden="true" />
              </div>
            ) : logo === zillowLogo ? (
              <img
                src={logo}
                alt="Zillow logo"
                className="h-[76px] w-[76px]"
                style={{
                  animation: `logo-glow-filter 2.4s ease-in-out ${index * 0.8}s infinite`,
                }}
              />
            ) : (
              <div
                className="rounded-full border border-stone-200 bg-white p-2.5 shadow-sm"
                style={{
                  animation: `logo-glow 2.4s ease-in-out ${index * 0.8}s infinite`,
                }}
              >
                <img
                  src={logo}
                  alt="Financial institution logo"
                  className="h-14 w-14 rounded-full object-contain"
                />
              </div>
            )}
          </div>
        ))}
      </div>

      <p
        className={`text-sm text-secondary transition-opacity duration-300 ease-in-out ${
          fade ? "opacity-100" : "opacity-0"
        }`}
      >
        {loadingMessages[messageIndex]}
      </p>

      <style>{`
        @keyframes logo-glow {
          0%, 100% {
            box-shadow: 0 0 0 0 rgba(0, 130, 81, 0);
            border-color: rgb(231 229 228);
          }
          50% {
            box-shadow: 0 0 12px 2px rgba(0, 130, 81, 0.25);
            border-color: rgb(0, 130, 81);
          }
        }

        @keyframes logo-glow-filter {
          0%, 100% {
            filter: drop-shadow(0 0 0 rgba(0, 130, 81, 0));
          }
          50% {
            filter: drop-shadow(0 0 6px rgba(0, 130, 81, 0.25));
          }
        }

        @keyframes line-sweep {
          0% {
            transform: scaleX(0);
            transform-origin: left;
          }
          45% {
            transform: scaleX(1);
            transform-origin: left;
          }
          55% {
            transform: scaleX(1);
            transform-origin: right;
          }
          100% {
            transform: scaleX(0);
            transform-origin: right;
          }
        }
      `}</style>
    </div>
  );
}
